import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { type Role, useRoles } from './useRoles';

type Props = {
  role: Role;
  /** Optional. When present, `RequireRole` renders it directly (legacy
   *  pattern — a route element wrapping a single page component). When
   *  absent, renders `<Outlet/>` for use as a layout route element that
   *  groups every gated child route:
   *    <Route element={<RequireRole role="Approver"/>}>
   *      <Route path="/approver/dashboard" element={<ApproverDashboard/>}/>
   *      …
   *    </Route>
   *  Same auth checks either way — the two shapes are interchangeable, so
   *  the file-per-route + shared-wrapper form on `App.tsx` can migrate in
   *  place without behavior change. */
  children?: ReactNode;
};

/**
 * Renders children (or Outlet) only when the current user holds exactly the
 * required role. Dual-role and no-role users are redirected to the generic
 * /auth/error page.
 */
export function RequireRole({ role, children }: Props) {
  const { role: actual, hasDualRole } = useRoles();

  if (hasDualRole) return <Navigate to="/auth/error?cause=dual-role" replace />;
  if (actual === null) return <Navigate to="/auth/error?cause=no-role" replace />;
  if (actual !== role) return <Navigate to={defaultRouteForRole(actual)} replace />;

  return children === undefined ? <Outlet /> : <>{children}</>;
}

export function defaultRouteForRole(role: Role): string {
  if (role === 'Approver') return '/approver/dashboard';
  if (role === 'Advisor') return '/advisor/dashboard';
  return '/originator/dashboard';
}
